/**
 * Deterministic stick-figure renderer for the Pose Assistant.
 *
 * Figures are described with a small vocabulary (stance, arms, legs, lean, tilt,
 * facing) that the backend normalises, so this component can never receive
 * junk geometry. Everything is drawn from the camera's point of view: `arm_l`
 * is the viewer's left, `arm_r` the viewer's right.
 */
import { POSES } from '@/constants/testIds';
import { keyedList } from '@/lib/keys';

const CELL = 64;
const H = 128;
const GROUND = 116;
const FG = '#F5F1E8';
const CREAM = '#E7DDCF';
const GOLD = '#C9A45C';
const MUTED = '#6E6A63';

// Arm targets relative to the shoulder point; `d` is +1 for the viewer-right arm
// and -1 for the viewer-left arm so arms mirror naturally.
const ARM = {
  down: (d) => [[d * 4, 16], [d * 5, 32]],
  hip: (d) => [[d * 11, 13], [d * 3, 28]],
  pocket: (d) => [[d * 9, 15], [d * 2, 33]],
  chest: (d) => [[d * 11, 13], [-d * 3, 11]],
  up: (d) => [[d * 10, -8], [d * 6, -24]],
  hair: (d) => [[d * 13, -1], [d * 2, -14]],
  holding: (d) => [[d * 8, 14], [d * 9, 24]],
  behind: (d) => [[d * 6, 14], [-d * 2, 27]],
};

function armPath(sx, sy, kind, d, partner) {
  if ((kind === 'waist_partner' || kind === 'shoulder_partner') && partner) {
    const target = kind === 'waist_partner' ? partner.hip : partner.shoulder;
    const ex = (sx + target[0]) / 2;
    const ey = Math.max(sy, target[1]) + 6;
    return { pts: [[sx, sy], [ex, ey], target], reach: true };
  }
  const rel = (ARM[kind] || ARM.down)(d);
  return { pts: [[sx, sy], [sx + rel[0][0], sy + rel[0][1]], [sx + rel[1][0], sy + rel[1][1]]], dashed: kind === 'behind' };
}

function poly(pts) {
  return pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
}

/** Compute the key points of a figure inside its cell (local coords). */
export function figureGeometry(fig) {
  const s = fig.scale ?? 1;
  const lean = (fig.lean ?? 0) * 8;
  const seated = fig.stance === 'sitting';
  const kneel = fig.stance === 'kneeling';
  const top = seated ? 28 : kneel ? 24 : 0; // how far the body drops toward the ground

  const cx = CELL / 2;
  const hipY = GROUND - (seated ? 34 : kneel ? 36 : 48) * s;
  const shoulderY = hipY - 34 * s;
  const headR = 7.5 * s;
  const headY = shoulderY - 12 * s - headR;
  const tilt = (fig.head_tilt ?? 0) * 4;

  const hip = [cx, hipY];
  const shoulder = [cx + lean, shoulderY];
  const head = [cx + lean * 1.3 + tilt, headY + top * 0];
  const shoulderL = [shoulder[0] - 10 * s, shoulder[1]];
  const shoulderR = [shoulder[0] + 10 * s, shoulder[1]];

  // Legs
  let legs;
  const kneeY = hipY + (GROUND - hipY) * 0.5;
  switch (fig.stance) {
    case 'sitting':
      legs = [
        [[cx, hipY], [cx - 14 * s, hipY + 2], [cx - 14 * s, GROUND]],
        [[cx, hipY], [cx + 12 * s, hipY + 4], [cx + 12 * s, GROUND]],
      ];
      break;
    case 'kneeling':
      legs = [
        [[cx, hipY], [cx - 6 * s, GROUND - 2], [cx - 16 * s, GROUND - 2]], // knee on ground, shin back
        [[cx, hipY], [cx + 12 * s, hipY + 10 * s], [cx + 12 * s, GROUND]],   // front foot planted
      ];
      break;
    case 'walking':
      legs = [
        [[cx, hipY], [cx - 8 * s, kneeY], [cx - 13 * s, GROUND]],
        [[cx, hipY], [cx + 9 * s, kneeY - 2], [cx + 12 * s, GROUND - 2]],
      ];
      break;
    default: {
      const spread = 6 * s;
      if (fig.legs === 'crossed') {
        legs = [
          [[cx, hipY], [cx + 2, kneeY], [cx + spread + 2, GROUND]],
          [[cx, hipY], [cx - 1, kneeY + 2], [cx - spread - 1, GROUND]],
        ];
      } else if (fig.legs === 'weight_back') {
        legs = [
          [[cx, hipY], [cx - spread, kneeY], [cx - spread - 1, GROUND]],
          [[cx, hipY], [cx + spread + 3, kneeY + 3], [cx + spread + 6, GROUND - 3]],
        ];
      } else if (fig.legs === 'step') {
        legs = [
          [[cx, hipY], [cx - spread, kneeY], [cx - spread - 4, GROUND - 1]],
          [[cx, hipY], [cx + spread + 4, kneeY - 2], [cx + spread + 8, GROUND + 2]],
        ];
      } else {
        legs = [
          [[cx, hipY], [cx - spread, kneeY], [cx - spread, GROUND]],
          [[cx, hipY], [cx + spread, kneeY], [cx + spread, GROUND]],
        ];
      }
    }
  }
  // Leaning: exaggerate torso offset and drop one shoulder
  if (fig.stance === 'leaning') {
    const dir = lean >= 0 ? 1 : -1;
    shoulder[0] += dir * 6;
    head[0] += dir * 7;
    shoulderL[0] += dir * 6;
    shoulderR[0] += dir * 6;
  }
  return { hip, shoulder, shoulderL, shoulderR, head, headR, legs, cx };
}

function Figure({ fig, index, count, neighbours }) {
  const g = figureGeometry(fig);
  const partnerFor = (side) => {
    // Reach toward the neighbour on the requested side; if the AI got left/right
    // mixed up and there is only a partner on the other side, reach there instead.
    const n = (side < 0 ? neighbours.left : neighbours.right) || (side < 0 ? neighbours.right : neighbours.left);
    return n ? { hip: [n.hip[0] + n.offset, n.hip[1]], shoulder: [n.shoulder[0] + n.offset, n.shoulder[1]] } : null;
  };
  const armL = armPath(g.shoulderL[0], g.shoulderL[1], fig.arm_l, -1, partnerFor(-1));
  const armR = armPath(g.shoulderR[0], g.shoulderR[1], fig.arm_r, +1, partnerFor(+1));

  // Facing indicator: a small nose tick on the side the head turns to.
  let noseDir = 0;
  if (fig.facing === 'left') noseDir = -1;
  else if (fig.facing === 'right') noseDir = 1;
  else if (fig.facing === 'partner') noseDir = neighbours.right ? 1 : neighbours.left ? -1 : 0;
  const away = fig.facing === 'away';

  return (
    <g transform={`translate(${index * CELL} 0)`} data-testid={POSES.figure(index)}>
      {/* ground shadow */}
      <ellipse cx={g.cx} cy={GROUND + 3} rx={14} ry={2.2} fill={GOLD} opacity={0.25} />
      {/* legs */}
      {g.legs.map((leg, i) => <polyline key={i === 0 ? 'leg-left' : 'leg-right'} points={poly(leg)} fill="none" stroke={FG} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />)}
      {/* torso */}
      <line x1={g.hip[0]} y1={g.hip[1]} x2={g.shoulder[0]} y2={g.shoulder[1]} stroke={FG} strokeWidth={2.8} strokeLinecap="round" />
      <line x1={g.shoulderL[0]} y1={g.shoulderL[1]} x2={g.shoulderR[0]} y2={g.shoulderR[1]} stroke={FG} strokeWidth={2.6} strokeLinecap="round" />
      {/* arms */}
      {[['arm-left', armL], ['arm-right', armR]].map(([side, a]) => (
        <polyline key={side} points={poly(a.pts)} fill="none" stroke={a.reach ? GOLD : FG} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" strokeDasharray={a.dashed ? '3 3' : undefined} opacity={a.dashed ? 0.6 : 1} />
      ))}
      {/* hands */}
      {[['hand-left', armL], ['hand-right', armR]].map(([side, a]) => <circle key={side} cx={a.pts[2][0]} cy={a.pts[2][1]} r={1.9} fill={a.reach ? GOLD : CREAM} />)}
      {/* neck + head */}
      <line x1={g.shoulder[0]} y1={g.shoulder[1]} x2={g.head[0]} y2={g.head[1] + g.headR} stroke={FG} strokeWidth={2.4} strokeLinecap="round" />
      <circle cx={g.head[0]} cy={g.head[1]} r={g.headR} fill={away ? '#2A2A2E' : CREAM} stroke={FG} strokeWidth={1.6} />
      {noseDir !== 0 && !away && <circle cx={g.head[0] + noseDir * (g.headR - 1)} cy={g.head[1] + 1} r={1.6} fill={GOLD} />}
      {/* label */}
      <text x={g.cx} y={H - 2} textAnchor="middle" fontSize="7" fill={MUTED} style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>{(fig.label || `subject ${index + 1}`).slice(0, 12)}</text>
    </g>
  );
}

export const PoseFigure = ({ figures = [], testId, className = '' }) => {
  const figs = figures.slice(0, 6);
  if (!figs.length) return null;
  const geos = figs.map((f, i) => ({ ...figureGeometry(f), offset: i * CELL }));
  const width = figs.length * CELL;
  return (
    <svg
      viewBox={`0 0 ${width} ${H}`}
      role="img"
      aria-label={`Stick-figure sketch of ${figs.map((f) => f.label).join(' and ')}`}
      data-testid={testId}
      className={`h-auto w-full ${className}`}
      style={{ maxWidth: `${Math.min(width * 1.6, 260)}px` }}
    >
      <line x1={4} y1={GROUND} x2={width - 4} y2={GROUND} stroke={MUTED} strokeWidth={0.8} strokeDasharray="2 3" />
      {keyedList(figs, (f) => f.label).map(({ key, item: f }, i) => (
        <Figure
          key={key}
          fig={f}
          index={i}
          count={figs.length}
          neighbours={{
            left: i > 0 ? { ...geos[i - 1], offset: -CELL } : null,
            right: i < figs.length - 1 ? { ...geos[i + 1], offset: CELL } : null,
          }}
        />
      ))}
    </svg>
  );
};
